import {connect} from 'react-redux';
import {Component} from 'react';
import PropTypes from 'prop-types';

import Step from '-/components/general/Step';
import {renderFieldErrors, isMobile} from '-/util/util';
import BuyerInfoService from '-/services/BuyerInfoService';
import VehicleInfoService from '-/services/VehicleInfoService';
import AdditionalIncomeService from '-/services/AdditionalIncomeService';
import EmploymentInfoService from '-/services/EmploymentInfoService';
import NavigationService from '-/services/NavigationService';
import CreditorService from '-/services/CreditorService';
import UIService from '-/services/UIService';
import SessionService from '-/services/SessionService';
import {ControlGroup} from '-/components/fields/Control';
import ApiService from '-/services/ApiService';
import {RadioGroup, Radio} from '-/components/fields/RadioGroup';
import Legend from '-/components/fields/Legend';
import Form from '-/components/fields/Form';
import AdditionalIncomeSection from '-/components/connected/steps/AdditionalIncomeSection';

class AdditionalIncome extends Component {
    static propTypes = {
        onSaveAndExitClick: PropTypes.func,
        onReviewClick: PropTypes.func
    };

    static defaultProps = {
        onSaveAndExitClick: () => {},
        onReviewClick: () => {}
    };

    constructor(props) {
        super(props);

        this.state = {
            errorMessages: [],
            showErrors: false
        };
        this.fieldRefs = {};

        this.onNextClick = this.onNextClick.bind(this);
        this.onSaveAndExitClick = this.onSaveAndExitClick.bind(this);
        this.onReviewClick = this.onReviewClick.bind(this);
        this.onInputChange = this.onInputChange.bind(this);
        this.onDeclareIncomeChange = this.onDeclareIncomeChange.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        const {isCoApplicant: oldIsCoApplicant} = this.props;
        const {isCoApplicant} = nextProps;
        if (isCoApplicant !== oldIsCoApplicant) {
            this.setState({
                errorMessages: [],
                showErrors: false
            });
        }
    }

    getErrorMessages() {
        const {entries = []} = this.props;

        const messages = [];
        entries.forEach((entry, i) => {
            const {amount, type} = entry;

            if (!amount || !type) {
                messages.push(`Amount and Type are required for entry ${i+1}`);
            }
        });

        return messages;
    }

    onInputChange() {
        setTimeout(() => {
            const {showErrors} = this.state;

            if (showErrors) {
                const errorMessages = this.getErrorMessages();
                this.setState({errorMessages});
            }
        }, 0);
    }

    onClickIfValid(fn) {
        const errorMessages = this.getErrorMessages();
        this.setState({errorMessages, showErrors: true});
        if (errorMessages.length <= 0) {
            fn();
        }
    }

    onSaveAndExitClick() {
        const {onSaveAndExitClick: fn} = this.props;

        fn();
    }

    onNextClick() {
        const {isCoApplicant, isPriorMemberAnswerRequired, requireCitizenshipProof = false} = this.props;

        let nextStep = isCoApplicant ? 'finalReview' : 'finalQuestions';

        if (requireCitizenshipProof) {
            nextStep = 'citizenship';
        } else if (isPriorMemberAnswerRequired) {
            nextStep = 'priorMembership';
        }

        this.onClickIfValid(() => {
            ApiService.save('additionalIncome').then(() => {
                UIService.stepCompleted('additionalIncome');
                NavigationService.next(nextStep);
            });
        });
    }

    onReviewClick() {
        const {onReviewClick: fn} = this.props;

        this.onClickIfValid(fn);
    }

    onDeclareIncomeChange(e) {
        const {value} = e.target;

        AdditionalIncomeService.set('hasAdditionalIncome', value === 'yes');
    }

    render() {
        const {
            apiErrors = [],
            filter = [],
            hasAdditionalIncome,
            onPreviousClick,
            status
        } = this.props;

        const {errorMessages, showErrors} = this.state;
        let typeFilter;

        if (status === 'retired') {
            typeFilter = type => !filter.includes(type);
        }

        return (
            <Step onSaveAndExitClick={this.onSaveAndExitClick} onPreviousClick={onPreviousClick} onNextClick={this.onNextClick} onReviewClick={this.onReviewClick}>
                <Form>
                    {hasAdditionalIncome && (
                        <AdditionalIncomeSection
                            addText="Add More Income"
                            mode="additionalIncome"
                            onInputChange={this.onInputChange}
                            ref={ref => this.fieldRefs.additionalIncomeSection = ref}
                            typeFilter={typeFilter} />
                    )}
                    {showErrors ? renderFieldErrors([...errorMessages, ...apiErrors]) : null}
                    <ControlGroup>
                        <Legend>Would you like to declare additional income?</Legend>
                        <p className="info subtle">Note that you are <strong>not required</strong> to disclose alimony, child support or separate maintenance income.</p>
                        <RadioGroup vertical={isMobile()}>
                            <Radio
                                name="yesNo"
                                label="Yes"
                                value="yes"
                                id="yes"
                                checked={hasAdditionalIncome}
                                onChange={this.onDeclareIncomeChange} />
                            <Radio
                                name="yesNo"
                                label="No"
                                value="no"
                                id="no"
                                checked={!hasAdditionalIncome}
                                onChange={this.onDeclareIncomeChange} />
                        </RadioGroup>
                    </ControlGroup>
                </Form>
            </Step>
        );
    }
}

const mapStateToProps = () => {
    const {status} = EmploymentInfoService.getEmploymentInfoForCurrentApplicant();
    const filter = ['retirementSocialSecurity', 'retirementPension'];
    const apiErrors = ApiService.getErrors();

    return {
        apiErrors,
        hasAdditionalIncome: AdditionalIncomeService.hasAdditionalIncome(filter),
        entries: AdditionalIncomeService.getFilteredEntries(filter, false),
        filter,
        isCoApplicant: NavigationService.isCoApplicant(),
        isCCRMode: SessionService.isCCRMode(),
        isPriorMemberAnswerRequired: CreditorService.getRequirePriorMembershipAnswer(),
        requireCitizenshipProof: CreditorService.getRequireCitizenshipProof(),
        offer: {
            vehicleInfo: VehicleInfoService.getReducerState(),
            ...EmploymentInfoService.getReducerState(),
            ...AdditionalIncomeService.getReducerState(),
            ...BuyerInfoService.getReducerState()
        },
        status
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onPreviousClick: () => {
            dispatch(() => {
                NavigationService.next('employmentInfo');
                ApiService.save('none');
            });
        },
        onSaveAndExitClick: () => {
            ApiService.explicitSave('additionalIncome');
        },
        onReviewClick: () => {
            NavigationService.next('finalReview');
        }
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(AdditionalIncome);
